import {Extension} from '@tiptap/core'

export type IndentOptions = {
  types: string[]
  indentUnit: number
  minIndent: number
  maxIndent: number
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType
      outdent: () => ReturnType
    }
  }
}

const Indent = Extension.create<IndentOptions>({
  name: 'indent',

  addOptions() {
    return {
      types: ['paragraph', 'heading'],
      indentUnit: 40,
      minIndent: 0,
      maxIndent: 200,
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const marginLeft = element.style.marginLeft
              if (marginLeft) {
                return parseInt(marginLeft, 10) || 0
              }
              return 0
            },
            renderHTML: (attributes) => {
              if (!attributes.indent || attributes.indent <= 0) {
                return {}
              }

              return {
                style: `margin-left: ${attributes.indent}px`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      indent: () => ({tr, state, dispatch}) => {
        const {selection} = state
        const {from, to} = selection

        let applied = false

        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!this.options.types.includes(node.type.name)) {
            return
          }

          const currentIndent = node.attrs.indent || 0
          const newIndent = Math.min(
            currentIndent + this.options.indentUnit,
            this.options.maxIndent,
          )

          if (newIndent !== currentIndent) {
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              indent: newIndent,
            })
            applied = true
          }
        })

        if (applied && dispatch) {
          dispatch(tr)
        }

        return applied
      },

      outdent: () => ({tr, state, dispatch}) => {
        const {selection} = state
        const {from, to} = selection

        let applied = false

        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!this.options.types.includes(node.type.name)) {
            return
          }

          const currentIndent = node.attrs.indent || 0
          const newIndent = Math.max(
            currentIndent - this.options.indentUnit,
            this.options.minIndent,
          )

          if (newIndent !== currentIndent) {
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              indent: newIndent,
            })
            applied = true
          }
        })

        if (applied && dispatch) {
          dispatch(tr)
        }

        return applied
      },
    }
  },
})

export default Indent
